/*
presetOptions.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useRedactionContext } from '../../providers/redactionProvider'
import { RedactProfile } from '../../modules/redactProfile'
import SwitchRow from '../../components/switchRow'
import { ProfileCustomField } from '../../modules/profileCustomField'
import { useState } from 'react'
import OverwriteModal from '../../components/Modals/overwriteModal'
import { isProfileDirty } from '../../modules/redactProfileHelper'
import { updatePreset } from '../../modules/httpClients/presetClient'
import { ProfileStandardField } from '../../modules/profileStandardField'
import { useToastContext } from '../../providers/useToastContext'
import { ToastType } from '../../types/toastTypes'
import ScrollWrapperLoader from '../../components/scrollWrapperLoader'
import { is5inches } from '../../modules/util'
import LinkSettingRow from '../Scan/ScanSettingRows/linkSettingRow'
import { errorTypes } from '../../constants/errorTypes'
import { useBaseContext } from '../../providers/baseProvider'
import { isAxiosError } from '../../modules/errors'

export default function ProfileItems () {
  const { t } = useTranslation()
  const redactionContext = useRedactionContext()
  const {
    selectedRedactProfile,
    setSelectedRedactProfile,
    draftRedactProfile,
    setDraftRedactProfile,
    resetDraftRedactProfile
  } = redactionContext
  const {setError} = useBaseContext();
  const [isOverwriteModalOpen, setIsOverwriteModalOpen] = useState(false)
  const navigate = useNavigate()
  const toastContext = useToastContext()

  const handleToggleStdField = (googleId: string) => {
    setDraftRedactProfile(prevState => {
      if (!prevState) return prevState
      const updatedStandardFields = prevState.standardFields.map(field =>
        updateToggledField(googleId, field)
      )
      return {
        ...prevState,
        standardFields: updatedStandardFields
      } as RedactProfile
    })
  }

  const updateToggledField = (
    googleId: string,
    field: ProfileStandardField
  ) => {
    if (field.googleId == googleId) {
      return { ...field, isToggled: !field.isToggled }
    }
    return field
  }

  const onContinue = () => {
    const anyStandardFieldSelected = draftRedactProfile?.standardFields.some(
      field => field.isToggled
    )
    if (
      !anyStandardFieldSelected &&
      draftRedactProfile?.customFields.length === 0
    ) {
      toastContext.addToast(
        t('STANDARD_REDACTION_CUSTOM_ITEM_MUST_BE_SELECTED'),
        ToastType.None,
        true,
        true
      )
      return
    }
    navigate('/scan')
  }
  const onCancel = () => {
    resetDraftRedactProfile()
    if (draftRedactProfile?.name) {
      navigate('/redactionType/preset')
    } else {
      navigate('/redactionType')
    }
  }
  const deleteCustomField = (customField: ProfileCustomField) => {
    setDraftRedactProfile(prevState => {
      if (!prevState) return prevState
      const updatedCustomFields =
        prevState.customFields.filter(cf => cf !== customField) || []
      return {
        ...prevState,
        customFields: updatedCustomFields
      } as RedactProfile
    })
  }

  const handleSavePreset = () => {
    if (
      !draftRedactProfile ||
      !isProfileDirty(selectedRedactProfile, draftRedactProfile)
    ) {
      toastContext.addToast(
        t('PRESET_NOT_MODIFIED'),
         ToastType.None,
        false,
        true
      )
      return
    }

    if (!draftRedactProfile.name || selectedRedactProfile?.isDefaultProfile) {
      navigate('/redactionItems/save')
    } else {
      setIsOverwriteModalOpen(true)
    }
  }

  const handleReset = () => {
    resetDraftRedactProfile()
    toastContext.addToast(
      t('SETTINGS_HAVE_BEEN_RESET'),
        ToastType.None,
      false,
      true
    )
  }

  const saveExistingPreset = () => {
    setIsOverwriteModalOpen(false)
    const showSaveErrorToast = () => {
      toastContext.addToast(
        t('ERROR.ERROR_HAS_OCCURRED'),
        ToastType.Danger,
        false,
        true
      )
    }

    if (!draftRedactProfile) {
      showSaveErrorToast()
      return
    }

    return updatePreset(draftRedactProfile)
      .then(() => {
        setSelectedRedactProfile(draftRedactProfile)
        toastContext.addToast(t('PRESET_SAVED'), ToastType.Success, false, true)
      })
      .catch(err => {
        if (isAxiosError(err)) {
          setError(errorTypes.apiError)
          return
        }
        showSaveErrorToast()
      })
  }
  return (
    <>
      <header className='xrx-navbar'>
        <div className='dn-header xrx-theme-appearance'>
          <div className='dn-inner'>
            <div className='dn-app-brand px-0'>
              <button
                type='button'
                className='xrx-btn btn-borderless btn-glyph btn-inline card-inline-button ml-3_75 mr-2'
                  onClick={onCancel}
              >
                <i className='xgl-navigate_left xglsize-32 modal-button-icon'></i>
              </button>
              <div className='dn-app-brand-label profile-header-label mt-0'>
                {!draftRedactProfile?.name
                  ? t('CUSTOM')
                  : (
                    <span className='header-profile-name'>
                      {`${t('PRESET')}: `}
                      {draftRedactProfile.name}
                    </span>
                  )}
              </div>
            </div>
          </div>
          <div className='nul-secondary-utility'>
            <button
              type='button'
              className='xrx-btn btn-borderless btn-glyph btn-inline card-inline-button ml-1 h-75'
              onClick={handleReset}
            >
              <i className='xgl-reload xglsize-32 modal-button-icon'></i>
            </button>
            {draftRedactProfile?.name && (
              <button
                type='button'
                className='xrx-btn btn-silver ml-2_8125'
                onClick={onCancel}
              >
                <i className='xgl-close xglsize-32 modal-button-icon'></i>
                <span className='btn-label'>{t('CANCEL')}</span>
              </button>
            )}
            <button
              type='button'
              className='xrx-btn btn-primary ml-2_8125 mr-3_75'
              onClick={onContinue}
            >
              <i className='xgl-navigate_right xglsize-32 modal-button-icon'></i>
              <span className='btn-label'>{t('NEXT')}</span>
            </button>
          </div>
        </div>
      </header>
      <div
        style={{
          width: is5inches ? 800 : 1024,
          height: is5inches ? 391 : 517
        }}
        className='content-wrapper'
      >
        <ScrollWrapperLoader>
          <div className='redaction-items-list content-section'>
            <ul className='xrx-list-group list-group-flush bg-white'>
              <li className='list-group-header d-flex justify-content-between'>
                <span className='label font-weight-bold' style={{fontSize:"1.33rem"}}>
                  {t('STANDARD_REDACTION_ITEMS')}
                </span>

                {draftRedactProfile?.name  && (
                  <span className='label'>
                    {t('LOCATION')}: {t('LOCATIONS.' + draftRedactProfile?.country.toUpperCase())}
                  </span>
                )}
              </li>
              {draftRedactProfile && !draftRedactProfile?.name && (
                <li className='list-group-item divider list-group-item-action'>
                  <LinkSettingRow
                    label={t('LOCATION')}
                    displayValue={t('LOCATIONS.' + draftRedactProfile?.country.toUpperCase())}
                    onClick={() => {
                      navigate('/redactProfile/selectLocation')
                    }}
                  />
                </li>
              )}
              {draftRedactProfile?.standardFields.map(standardField => (
                <li
                  key={standardField.id}
                  className='list-group-item divider list-group-item-action'
                  tabIndex={0}
                  onClick={() => handleToggleStdField(standardField.googleId)}
                >
                  <SwitchRow
                    setting={standardField.displayText}
                    value={standardField.isToggled}
                    setValue={() =>
                      handleToggleStdField(standardField.googleId)
                    }
                    key={standardField.id}
                  />
                </li>
              ))}
              <li
                className='list-group-item divider list-group-item-action modify-list-item'
                tabIndex={0}
                onClick={() => {
                  return navigate('edit')
                }}
              >
                <div className='left-object custom-icon-rounded'>
                  <i className='xgl-edit xglsize-32'></i>
                </div>

                <div className='content-container'>
                  <div className='left-label pl-0'>
                    <span>{t('MODIFY_LIST')}</span>
                  </div>
                </div>
              </li>
              <li className='list-group-header d-flex justify-content-between'>
                <span className='label font-weight-bold' style={{fontSize:"1.33rem"}}>
                  {t('CUSTOM_ITEMS')}
                </span>
              </li>
              <li
                className='list-group-item divider list-group-item-action'
                onClick={() => {
                  return navigate('custom/add')
                }}
              >
                <div className='left-object custom-icon-rounded'>
                  <i className='xgl-add_circle_two_tone xglsize-32'></i>
                </div>
                <div className='content-container'>
                  <div className='left-label pl-0'>
                    <span>{t('ADD_CUSTOM_WORD_OR_PHRASE')}</span>
                  </div>
                </div>
              </li>
              {draftRedactProfile?.customFields.map((customWord, index) => (
                <li
                  className='list-group-item divider'
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`custom/edit/${index}`)}
                >
                  <div className='content-container'>
                    <div className='left-label pl-0'>
                      <span className='overflow-ellipsis' style={{width: is5inches ? 708 : 930}}>{customWord.text}</span>
                    </div>
                                      <div className="right-object mx-3_75">
                  <button
                    className='xrx-btn btn-borderless btn-glyph btn-inline card-inline-button'
                    onClick={(e) => { e.stopPropagation(); deleteCustomField(customWord); }}
                  >
                    <i className='xgl-subtract_circle_two_tone xglsize-32 modal-button-icon'></i>
                  </button>
                  </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className='col-12 p-3'>
              <button
                type='button'
                className='xrx-btn col-12 px-0 centered-content'
                onClick={handleSavePreset}
              >
                <span className='btn-label font-size-26 col-12 text-center'>
                  {t('SAVE_PRESET')}
                </span>
              </button>
            </div>
          </div>
        </ScrollWrapperLoader>
      </div>
                  <OverwriteModal
              isOpen={isOverwriteModalOpen}
              onClose={() => setIsOverwriteModalOpen(false)}
              disableOverwrite={draftRedactProfile?.isDefaultProfile}
              onOverwrite={saveExistingPreset}
              onSaveAsNew={() => navigate('/redactionItems/save')}
              presetName={draftRedactProfile ? draftRedactProfile.name : ''}
            />
    </>
  )
}
